import { isAfter, isBefore, isValid, parse } from 'date-fns';

export const stripHtml = (html: string) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  const stripped = tmp.textContent || tmp.innerText || '';

  return stripped;
};

export type Validator<
  T extends Record<string, unknown> = Record<string, unknown>,
> = (item: T) => string | null | undefined;

export const checkRequired = <T extends Record<string, unknown>>(
  key: keyof T,
  msg: string = "Can't be blank",
): Validator<T> => {
  return (item: T) => {
    const value = item[key];
    if (
      value === null ||
      value === undefined ||
      value === '' ||
      (typeof value === 'object' && !Object.values(value).length)
    ) {
      return msg;
    }
    return null;
  };
};

export const checkRequiredArray = <T extends Record<string, unknown>>(
  key: keyof T,
  msg: string = "Can't be blank",
): Validator<T> => {
  return (item: T) => {
    const value = item[key];
    if (
      !value ||
      !Array.isArray(value) ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return msg;
    }
    return null;
  };
};

export const checkNumberRequired = <T extends Record<string, unknown>>(
  key: keyof T,
  msg: string = "Can't be blank",
): Validator<T> => {
  return (item: T) => {
    const value = item[key];
    const invalidValues: unknown[] = [null, NaN, undefined, ''];

    if (invalidValues.includes(value)) {
      return msg;
    }
    return null;
  };
};

//msg text is hardcoded. it should be changed on implement localization task for client pages
export const validateCurrency = <T extends Record<string, unknown>>(
  key: keyof T,
): Validator<T> => {
  return (item: T) => {
    const value = item[key];
    if (!value || typeof value !== 'string') {
      return 'Поле не може бути пустим';
    }

    if (value.includes('.')) {
      return 'Сума повинна бути цілою';
    }

    return null;
  };
};

export const passwordValidator = <
  T extends { password: string; password_confirmation: string },
>(): Validator<T> => {
  return (item: T) => {
    if (item.password.length < 6 || item.password_confirmation.length < 6) {
      return 'пароль повинен мати не менше 6 символів';
    }

    if (item.password !== item.password_confirmation) {
      return 'паролі не співпадають';
    }

    return null;
  };
};

export const isDateValid = <T extends Record<string, unknown>>(
  key: keyof T,
  msg: string = 'Дата вказана в невірному форматі',
): Validator<T> => {
  return (item: T) => {
    const value = item[key];

    if (!value || typeof value !== 'string') {
      return undefined;
    }

    try {
      const parsedDate = parse(value, 'yyyy-MM-dd', new Date());
      if (!isValid(parsedDate)) {
        return msg;
      }

      const minDate = new Date('1900-01-01');
      const maxDate = new Date('2100-01-01');
      if (isBefore(parsedDate, minDate) || isAfter(parsedDate, maxDate)) {
        return msg;
      }
    } catch {
      return msg;
    }

    return undefined;
  };
};

type CheckLengthOptions<T extends Record<string, unknown>> = {
  key: keyof T;
  minLength?: number;
  maxLength?: number;
  minMsg?: string;
  maxMsg?: string;
};

export const checkLength = <T extends Record<string, unknown>>(
  options: CheckLengthOptions<T>,
): Validator<T> => {
  const {
    key,
    minLength = 2,
    maxLength = 15,
    minMsg = `Мінімальна довжина ${minLength} символів`,
    maxMsg = `Максимальна довжина ${maxLength} символів`,
  } = options;

  return (item: T) => {
    const value = item[key];
    if (!value || typeof value !== 'string') {
      return null;
    }

    const trimmedValue = String(value).trim();

    if (minLength !== undefined && trimmedValue.length < minLength) {
      return minMsg;
    }

    if (maxLength !== undefined && trimmedValue.length > maxLength) {
      return maxMsg;
    }

    return null;
  };
};
